import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useFeatureFlags } from './utils/featureFlags';
import './App.css';
import { ThemeProvider } from './components/basic/ThemeProvider';
import NextGenLanding from './components/features/LandingWithSections';
import GameShowPage from './components/features/GameShow/components/GameShowPage';
import CastPage from './components/features/Cast/components/CastPage';
import { NewNGPage } from './components/features/NewNG/components/NewNGPage';
import { NewNGProvider } from './components/features/NewNG/context/NewNGContext';
import { TheatrePage } from './components/features/NewNG/components/TheatrePage';
import NewsEditor from './components/features/NewsEditor';
import NewsEditorProvider from './components/features/NewsEditor/context/NewsEditorContext';
import ComingSoonPage from './ComingSoonPage';
import { AuthProvider, AuthRouter } from './components/features/Auth';


// Only import DebugConsole in development
const DebugConsole = process.env.NODE_ENV === 'development'
  ? lazy(() => import('./components/basic/DebugConsole'))
  : () => null;

const Blog = lazy(() => import('./components/features/Blog'));
const DocsPage = lazy(() => import('./components/features/Docs/components/DocsPage'));
const PlaygroundPage = lazy(() => import('./components/features/Playground'));
const MusicPlayer = lazy(() => import('./components/features/MusicPlayer'));
const DeepVision = lazy(() => import('./components/features/DeepVision'));
const CinemaPage = lazy(() => import('./components/features/Cinema/components/CinemaPage'));

function App() {
  const { debugConsole } = useFeatureFlags();
  const performanceMetricsRef = useRef<string[]>([]);
  const hasLoggedMetrics = useRef(false);

  // Get initial theme from localStorage or default to light
  const initialTheme = localStorage.getItem('theme') || 'dark';

  // Collect performance metrics early
  useEffect(() => {
    if (!debugConsole) return;

    const collectMetrics = () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const paint = performance.getEntriesByType('paint');
      const firstPaint = paint.find(entry => entry.name === 'first-paint');
      const firstContentfulPaint = paint.find(entry => entry.name === 'first-contentful-paint');

      const metrics = [
        'Performance Metrics:',
        `DNS lookup: ${Math.round(navigation.domainLookupEnd - navigation.domainLookupStart)}ms`,
        `Connection time: ${Math.round(navigation.connectEnd - navigation.connectStart)}ms`,
        `Response time: ${Math.round(navigation.responseEnd - navigation.requestStart)}ms`,
        `DOM Interactive: ${Math.round(navigation.domInteractive - navigation.startTime)}ms`,
        `DOM Complete: ${Math.round(navigation.domComplete - navigation.startTime)}ms`,
        `Load Event: ${Math.round(navigation.loadEventEnd - navigation.startTime)}ms`,
        `First Paint: ${Math.round(firstPaint?.startTime || 0)}ms`,
        `First Contentful Paint: ${Math.round(firstContentfulPaint?.startTime || 0)}ms`
      ];

      performanceMetricsRef.current = metrics;
    };

    // Collect metrics after a short delay to ensure all timings are available
    setTimeout(collectMetrics, 0);
  }, [debugConsole]);

  // Log metrics when debug function becomes available
  useEffect(() => {
    const logMetricsInterval = setInterval(() => {
      const debug = window.debug;
      if (typeof debug === 'function' && performanceMetricsRef.current.length > 0 && !hasLoggedMetrics.current) {
        performanceMetricsRef.current.forEach(metric => debug(metric));
        hasLoggedMetrics.current = true;

        // Set up performance observer for long tasks
        const observer = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            debug(`Long Task detected: ${Math.round(entry.duration)}ms`);
          });
        });

        observer.observe({ entryTypes: ['longtask'] });
        clearInterval(logMetricsInterval);
        return () => observer.disconnect();
      }
    }, 100); // Check every 100ms

    return () => clearInterval(logMetricsInterval);
  }, []);

  return (
    <ThemeProvider themeName={initialTheme as 'light' | 'dark'}>
      <Router basename="/game">
        <div className="app">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<NextGenLanding />} />
              <Route path="/gameshow" element={<GameShowPage />} />
              <Route path="/cast/*" element={<CastPage />} />
              <Route
                path="/auth/*"
                element={
                  <AuthProvider>
                    <AuthRouter />
                  </AuthProvider>
                }
              />
              <Route path="/news/*" element={
                <NewNGProvider>
                  <TheatrePage />
                </NewNGProvider>
              } />
              <Route path="/newsng" element={
                <NewNGProvider>
                  <NewNGPage />
                </NewNGProvider>
              } />
              <Route path="/newsng-theatre" element={
                <NewNGProvider>
                  <TheatrePage />
                </NewNGProvider>
              } />
              <Route path="/news-editor" element={
                <NewsEditorProvider>
                  <NewsEditor />
                </NewsEditorProvider>
              } />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/studio" element={
                <AuthProvider>
                  <PlaygroundPage />
                </AuthProvider>
              } />
              <Route path="/blog/*" element={<Blog />} />
              <Route path="/music" element={
                <AuthProvider>
                  <MusicPlayer />
                </AuthProvider>
              } />
              <Route path="/vision" element={
                <AuthProvider>
                  <DeepVision />
                </AuthProvider>
              } />
              <Route path="/cinema" element={
                <AuthProvider>
                  <CinemaPage />
                </AuthProvider>
              } />
              <Route path="/vx" element={
                <ComingSoonPage
                  title="ViralX - Coming Soon"
                  description="Launch your AI cast into viral-aware real-time X broadcasts. Capture moments as they happen. Be the algorithm."
                />
              } />
              <Route path="/plot" element={
                <ComingSoonPage
                  title="CineMatrix - Coming Soon"
                  description="Cognitive media rendered by MediaGen."
                />
              } />
            </Routes>
          </Suspense>
          {debugConsole && (
            <Suspense fallback={null}>
              <DebugConsole initialVisible={false} />
            </Suspense>
          )}
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
